import { FormGroup, FormBuilder, Validators, ValidatorFn } from '@angular/forms';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    OnInit
} from '@angular/core';
import { FieldSchema, StageSchema, StringFieldOption } from '@lsq-frontend/remote-services/ar-app';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { IFieldPair } from './dynamic-container-base.component.interfaces';

@Component({
    selector: 'dynamic-container-base',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: ``
})
export class DynamicContainerBase implements OnChanges, OnInit {
    @Input() stageSchema: StageSchema;
    @Input() prospectFields: { [key: string]: any };
    @Input() formGroup: FormGroup;
    @Input() rowView: Boolean;
    @Input() reviewMode: Boolean;
    @Input() adminMode: Boolean;
    @Input() prospectDataId: string;
    @Input() viewedBy: string = 'both';
    @Input() schemaType: string;
    public FieldTypeEnum = FieldSchema.FieldTypeEnum;
    public readonly debug: Boolean = false;
    public pairs: IFieldPair[];

    constructor(private fb: FormBuilder) {}

    public passwordsMustMatchCopy = `"Create Password" and "Confirm Password" must match.`;

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.stageSchema) {
            if (!this.adminMode) {
                //NG0100: ExpressionChangedAfterItHasBeenCheckedError will be triggered if we run this in adminMode
                this.createForm(this.stageSchema?.fieldSchemas || []);
            }
            this.pairs = this.buildProspectSchemaPairs();
        }
    }

    public ngOnInit(): void {
        this.pairs = this.buildProspectSchemaPairs();
    }

    public formValidators(Field: FieldSchema): ValidatorFn[] {
        const validatorsToAdd = [];
        if (Field.isRequired) {
            validatorsToAdd.push(Validators.required);
        }
        for (const [key, value] of Object.entries(Field.fieldOption ?? {})) {
            switch (key) {
                case 'min':
                    validatorsToAdd.push(Validators.min(value));
                    break;
                case 'max':
                    validatorsToAdd.push(Validators.max(value));
                    break;
                case 'requiredTrue':
                    if (value) {
                        validatorsToAdd.push(Validators.requiredTrue);
                    }
                    break;
                case 'inputType':
                    if (value === StringFieldOption.InputTypeEnum.Email) {
                        validatorsToAdd.push(LsqValidators.email);
                    } else if (value === StringFieldOption.InputTypeEnum.Tel) {
                        validatorsToAdd.push(LsqValidators.phone);
                    } else if (value === StringFieldOption.InputTypeEnum.Password) {
                        validatorsToAdd.push(LsqValidators.strictPassword);
                    }
                    break;
                case 'minLength':
                    validatorsToAdd.push(LsqValidators.minLength(value));
                    break;
                case 'maxLength':
                    validatorsToAdd.push(LsqValidators.maxLength(value));
                    break;
                case 'pattern':
                    validatorsToAdd.push(Validators.pattern(value));
                    break;
                case 'nullValidator':
                    if (value) {
                        validatorsToAdd.push(Validators.nullValidator);
                    }
                    break;
                default:
                    break;
            }
        }
        return validatorsToAdd;
    }

    public createForm(fieldSchemas: FieldSchema[]): void {
        let i = 0;
        let password1;
        let password2;

        for (const Field of fieldSchemas) {
            for (const [key, value] of Object.entries(Field.fieldOption ?? {})) {
                if (key === 'inputType' && value === 'Password') {
                    i++;
                    switch (i) {
                        case 1:
                            password1 = Field.name;
                            break;
                        case 2:
                            password2 = Field.name;
                            break;
                    }
                }
            }
        }

        for (const Field of fieldSchemas) {
            const validatorsToAdd = this.formValidators(Field);
            if (Field.name === password1) {
                validatorsToAdd.push(
                    LsqValidators.matchValueSource(
                        () => this.formGroup?.get(password2),
                        this.passwordsMustMatchCopy
                    )
                );
            } else if (Field.name === password2) {
                validatorsToAdd.push(
                    LsqValidators.matchValueSource(
                        () => this.formGroup?.get(password1),
                        this.passwordsMustMatchCopy
                    )
                );
            }
            this.formGroup.addControl(Field.name, this.fb.control('', validatorsToAdd));
        }
    }

    public buildProspectSchemaPairs(): IFieldPair[] {
        const pairsFieldSchemas: IFieldPair[] = [];
        var showDynamicField: Boolean = false;
        for (const fieldSchema of this.stageSchema?.fieldSchemas) {
            showDynamicField =
                this.reviewMode ||
                !!!fieldSchema.viewedBy ||
                fieldSchema.viewedBy === this.viewedBy ||
                fieldSchema.viewedBy === 'both';
            if (showDynamicField) {
                pairsFieldSchemas.push({
                    fieldSchema: fieldSchema,
                    fieldData: this.prospectFields[fieldSchema.id]
                });
            }
        }
        return pairsFieldSchemas;
    }
}
